import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { legendClasses } from "@mui/x-charts/ChartsLegend";
import { PieChart } from "@mui/x-charts/PieChart";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { formatHoursMinutes } from "@repo/legacy/components/helper/Index";
import { useGetMyProjectWorktimeYearlyOverviewLazyQuery } from "@repo/server-api";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";

export const ProjectWorktimeYearOverview = () => {
	const { t, i18n } = useTranslation();

	const theme = useTheme();

	const [year, setYear] = useState<dayjs.Dayjs>(dayjs());
	const [openYear, setOpenYear] = useState<boolean>(false);

	const [getMyProjectWorktimeYearylyOverview] = useGetMyProjectWorktimeYearlyOverviewLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [data, setData] = useState<
		{
			label: string;
			value: number;
			color?: string;
		}[]
	>([]);

	useEffect(() => {
		getMyProjectWorktimeYearylyOverview({
			variables: {
				year: year.year(),
			},
		}).then((result) => {
			const res = result.data?.myProjectWorktimeYearlyOverview || [];
			const chartData = res.map(
				(
					item: { projectName: string; projectPositionName: string; totalMinutes: number },
					idx: number,
				): { label: string; value: number; color?: string } => ({
					label: item.projectName + " - " + item.projectPositionName,
					value: item.totalMinutes,
					color: theme.graphPalette[idx % theme.graphPalette.length],
				}),
			);

			setData(chartData);
		});
	}, [getMyProjectWorktimeYearylyOverview, year]);

	const settings = {
		margin: { right: 5 },
		width: 200,
		height: 200,
	};

	return (
		<Box
			sx={{
				border: "1px solid #ccc",
				paddingTop: 0,
				paddingBottom: 2,
				paddingLeft: 2,
				paddingRight: 2,
				borderRadius: 2,
				marginRight: 2,
				height: "100%",
			}}>
			<h3 className="purpleHeader">
				{t("worked-hours")}
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language || "de"}>
					<DatePicker
						sx={{ marginTop: "-5px" }}
						className="right w-20"
						open={openYear}
						onClose={() => {
							setOpenYear(false);
						}}
						onOpen={() => {
							setOpenYear(true);
						}}
						label={t("year")}
						views={["year"]}
						value={year}
						onChange={(newValue) => {
							setYear(newValue as dayjs.Dayjs);
						}}
						slotProps={{
							textField: {
								onClick: () => {
									setOpenYear(!openYear);
								},
							},
						}}
					/>
				</LocalizationProvider>
			</h3>
			<PieChart
				localeText={{ noData: t("no-data-to-display") }}
				slotProps={{
					legend: {
						direction: "horizontal",
						position: {
							vertical: "bottom",
							horizontal: "center",
						},
						sx: {
							gap: "16px",
							[`.${legendClasses.mark}`]: {
								height: 15,
								width: 15,
							},
							[".MuiChartsLegend-series"]: {
								gap: "8px",
							},
						},
					},
				}}
				series={[
					{
						innerRadius: 50,
						outerRadius: 100,
						data,
						valueFormatter: ({ value }) => formatHoursMinutes(value),
					},
				]}
				{...settings}
			/>
		</Box>
	);
};
